import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import LaptopChromebookOutlinedIcon from '@mui/icons-material/LaptopChromebookOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Reality = { icon: SvgIconComponent; title: string; body: string };

const realities: Reality[] = [
  {
    icon: SavingsOutlinedIcon,
    title: 'Budgets are public, fixed, and scrutinized',
    body: 'A school\'s IT budget is usually set once a year and answers to a board, not a flexible line item you can renegotiate mid-year when a vendor raises prices.',
  },
  {
    icon: SchoolOutlinedIcon,
    title: 'The data is a minor\'s, and the rules are federal',
    body: 'Student education records and anything touching a child under 13 online fall under FERPA and COPPA specifically — not general-purpose "handle data responsibly" language.',
  },
  {
    icon: LaptopChromebookOutlinedIcon,
    title: '1:1 device fleets are large relative to staff headcount',
    body: 'A district with a few hundred students often has a few hundred Chromebooks or iPads and a tiny IT team — device-to-technician ratios most MSPs\' commercial clients never approach.',
  },
  {
    icon: CalendarMonthOutlinedIcon,
    title: 'The maintenance calendar is the academic calendar',
    body: 'Patch windows, re-imaging, and infrastructure work happen around a school year, not a standard business week — summer is a deployment season, not a slow one.',
  },
];

export function SolutionsEducation(): JSX.Element {
  useSeo({
    title: 'For MSPs serving K-12 schools',
    description:
      'Built by an MSP that dogfoods Nexus on real K-12 school clients — FERPA/COPPA-aware compliance tracking, 1:1 device fleet management, and a platform priced for public-sector budgets.',
    path: '/solutions/education',
  });

  return (
    <div>
      <PageHero
        eyebrow="For MSPs serving schools"
        title="Built by an MSP that runs its own practice on real school clients."
        subtitle="Nexus isn't a generic platform with an education vertical bolted on. Part of the MSP practice we dogfood Nexus on serves K-12 school clients directly, which is why FERPA and COPPA aren't abstract line items on our compliance framework list."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
                What&apos;s actually different about a school client
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Schools aren&apos;t a smaller version of a commercial client.
              </h2>
              <p className="mt-4 text-white/70">
                They&apos;re a different shape of client entirely — and a platform built only for
                commercial IT tends to show the gaps quickly.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {realities.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.title} delay={i * 90}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{r.title}</h3>
                    <p className="mt-1.5 text-sm text-white/65">{r.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.015] py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
              What that means inside Nexus
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[32px]">
              Not a separate education product — the same platform, used the way we use it ourselves.
            </h2>
            <ul className="mt-6 flex flex-col gap-3">
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/compliance/ferpa-coppa" className="text-nexus-purple-light no-underline hover:underline">
                    FERPA/COPPA posture tracking
                  </Link>{' '}
                  is one of the frameworks the compliance module tracks natively — not a request we&apos;d
                  have to build from scratch for a school client.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/platform/asset-device-management" className="text-nexus-purple-light no-underline hover:underline">
                    Asset &amp; device management
                  </Link>{' '}
                  handles check-out/check-in at 1:1 fleet scale — the same lifecycle tracking a
                  200-Chromebook rollout needs, not a feature designed only for a 30-laptop office.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  The{' '}
                  <Link to="/platform/client-staff-portal" className="text-nexus-purple-light no-underline hover:underline">
                    client &amp; staff portal
                  </Link>{' '}
                  gives non-technical school staff — often the actual day-to-day contact, not a
                  dedicated IT director — a simple way to submit and track a ticket.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/for-small-msps" className="text-nexus-purple-light no-underline hover:underline">
                    Consolidation
                  </Link>{' '}
                  matters even more against a fixed, board-approved budget than an unpredictable
                  commercial one — one platform is a number you can actually defend at a budget
                  meeting.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/integrations/google-workspace" className="text-nexus-purple-light no-underline hover:underline">
                    Google Workspace sync
                  </Link>{' '}
                  pulls the managed Chromebook fleet straight out of the Admin console, so a
                  200-device 1:1 rollout doesn&apos;t mean re-keying every serial number by hand.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/compliance/state-student-privacy-laws" className="text-nexus-purple-light no-underline hover:underline">
                    State student-privacy law tracking
                  </Link>{' '}
                  sits alongside the federal FERPA/COPPA framework — most districts answer to both,
                  not just the federal baseline.
                </span>
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
              Further reading
            </span>
            <h2 className="mt-3 font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              What actually differs when the client is a K-8 district
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              E-rate funding cycles, 1:1 device lifecycle, and a maintenance window that closes
              the day school reopens — we wrote up the specific ways a school client differs from
              a generic SMB in{' '}
              <Link
                to="/blog/what-k8-schools-need-from-an-msp"
                className="text-nexus-purple-light no-underline hover:underline"
              >
                what K-8 schools actually need from an MSP
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-content max-w-[760px] text-center">
          <Reveal>
            <Chip
              label="Honest about scope"
              size="small"
              className="mb-4 border border-nexus-purple/30 bg-nexus-purple/[0.14] font-semibold text-nexus-purple-light"
            />
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              We&apos;re not pitching an &quot;education edition.&quot;
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              Nexus is one platform, not a vertical-specific fork — FERPA/COPPA is one framework
              among the several the compliance module tracks, alongside SOC 2, NIST CSF, HIPAA,
              PCI-DSS, and ISO 27001. What&apos;s genuinely different for a school client is that we
              built and use this platform against real K-12 workloads ourselves before ever
              describing it as ready for yours.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Run your school clients on the platform we run ours on.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort — we&apos;ll talk through what a district or
                private-school client actually needs, not a generic pitch.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
